import React, { useEffect } from "react";
import styled from "styled-components";
import { useLocation, Link } from "react-router-dom";
import { device } from "../lib/breakpoints";
import { APP_NAME } from "../helpers/constants";
import { Spacer } from "../styles/CommonElements";
import SignUpCta from "./general/SignUpCta";
import SEO from "../components/SEO";
import { generateMissionSchema } from "../lib/seoConfig";
import Logo from "../components/general/Logo";

const founderBeforeImage = "/images/mission/founder-before-2.svg";
const founderAfterImage = "/images/mission/founder-after-2.svg";

export default function OurMission() {
  const location = useLocation();

  useEffect(() => {
    window.scroll(0, 0);
  }, [location.pathname]);

  return (
    <Container>
      <SEO
        title={`Our Mission | ${APP_NAME} - Empowering Independent Landlords`}
        description={`Learn about ${APP_NAME}'s mission to transform property management for independent landlords through AI-powered automation, rent collection, and tenant communication.`}
        keywords={`property management mission, landlord platform mission, AI property management, independent landlord tools, property management innovation, rental property technology mission`}
        imageAlt={`${APP_NAME} mission to support independent landlords`}
        schema={generateMissionSchema()}
        category="Property Management"
        tags={[
          "property management mission",
          "landlord platform mission",
          "AI property management",
          "independent landlord tools",
        ]}
      />
      <HeroSection>
        <HeroContent>
          <HeroLogoCircle>
            <Logo
              color="white"
              displayMode="icon"
              size="large"
              linkTo={null}
              className="landlordops-logo"
            />
          </HeroLogoCircle>
          <PageTitle>
            Calmer operations.
            <br />
            Stronger rentals.
          </PageTitle>
          <Subtitle>
            {APP_NAME} helps independent landlords stay organized: rent, repairs,
            and tenant communication in one system, so small problems do not
            turn into all-day fire drills.
          </Subtitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <Section>
          <SectionTitle>The Problem</SectionTitle>
          <ProblemText>
            <Emphasis>
              Property management is a thousand small jobs, and they all show up
              in your inbox.
            </Emphasis>
          </ProblemText>
          <Paragraph>
            Rent reminders, repair requests, lease questions, and follow-ups
            do not wait for a convenient time. When systems are scattered, the
            work becomes reactive: things slip, tenants get frustrated, and the
            owner becomes the default help desk.
          </Paragraph>
          <Paragraph>
            {APP_NAME} exists because independent landlords deserve tools that
            match the real workload, not dashboards built for a different
            industry.
          </Paragraph>
        </Section>

        <Spacer $marginTop="4rem" />

        <Section>
          <SectionTitle>The Spark</SectionTitle>
          <Paragraph>
            The through-line is simple: reduce coordination overhead. When a
            tenant can get a clear answer fast, and when a landlord can see
            status at a glance, small issues stay small.
          </Paragraph>
          <Paragraph>
            I asked myself: <br />
            <Emphasis>
              "What if independent landlords had the same modern tooling larger
              operators take for granted?"
            </Emphasis>
          </Paragraph>
          <Paragraph>
            {APP_NAME} started from that idea: a platform focused on the daily
            work of running rentals, not a generic "business app" that happens
            to have a few property features.
          </Paragraph>
        </Section>

        <Spacer $marginTop="4rem" />

        <Section>
          <SectionTitle>The Solution</SectionTitle>
          <SolutionText>
            <Emphasis>
              {APP_NAME} is built to help independent landlords and small teams:
            </Emphasis>
          </SolutionText>
          <BenefitsList>
            <BenefitItem>✅ See rent and exceptions without digging</BenefitItem>
            <BenefitItem>✅ Keep tenant issues tracked end-to-end</BenefitItem>
            <BenefitItem>✅ Use AI to answer routine questions consistently</BenefitItem>
            <BenefitItem>
              ✅ Operate with fewer tools and less context switching
            </BenefitItem>
          </BenefitsList>

          <ResultsText>
            <Emphasis>Teams use {APP_NAME} to:</Emphasis>
          </ResultsText>
          <ResultsList>
            <ResultItem>
              • <Emphasis>Save time on repeat tenant questions</Emphasis>
            </ResultItem>
            <ResultItem>
              • <Emphasis>Stay ahead of small maintenance issues</Emphasis>
            </ResultItem>
            <ResultItem>
              • <Emphasis>Run a more professional resident experience</Emphasis>
            </ResultItem>
          </ResultsList>
        </Section>

        <Spacer $marginTop="4rem" />

        <CTASection>
          <CTATitle>See if {APP_NAME} fits your portfolio</CTATitle>
          <CTASubtitle>
            If you are an independent landlord looking for a calmer, more
            modern workflow, we would love to help you evaluate next steps.
          </CTASubtitle>
          <CTABenefits>
            <CTABenefit>✅ Property-first workflows, not generic CRMs</CTABenefit>
            <CTABenefit>✅ Clear paths for rent, repairs, and messaging</CTABenefit>
            <CTABenefit>✅ Built for owners who want fewer tools, not more</CTABenefit>
          </CTABenefits>
          <CTAButton href="/contact">Talk to us →</CTAButton>
        </CTASection>
      </ContentSection>
    </Container>
  );
}

const Container = styled.div`
  width: 100%;
  background: var(--color-background);
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at 30% 20%,
      rgba(205, 175, 99, 0.03) 0%,
      transparent 50%
    );
    pointer-events: none;
  }
`;

const CompanyName = styled.span`
  font-weight: 700;
  color: var(--color-secondary);
`;

const HeroSection = styled.div`
  position: relative;
  min-height: 70vh;
  background: linear-gradient(100deg, #181818 0%, #181818 75%, #cdaf63 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding-top: 4vh;
  padding-bottom: 8vh;
  text-align: center;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    background: radial-gradient(
        ellipse 60% 40% at 50% 35%,
        rgba(255, 215, 0, 0.1) 0%,
        rgba(255, 215, 0, 0.04) 50%,
        transparent 100%
      ),
      url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.02'/%3E%3C/svg%3E");
    z-index: 1;
  }

  @media ${device.mobile} {
    min-height: 56vh;
    padding-top: 2vh;
    padding-bottom: 4vh;
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    120deg,
    rgba(205, 175, 99, 0.82) 0%,
    rgba(246, 236, 200, 0.55) 60%,
    rgba(138, 168, 143, 0.7) 100%
  );
  z-index: 2;
`;

const ContrastOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    120deg,
    rgba(0, 0, 0, 0.18) 0%,
    rgba(0, 0, 0, 0.1) 100%
  );
  z-index: 2;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 3;
  text-align: center;
  color: var(--color-text-white);
  max-width: none;
  margin: 0 auto;
  padding: 0;

  @media ${device.mobile} {
    padding: 0;
  }
`;

const HeroLogoCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: none;
  box-shadow: none;
  position: relative;
  z-index: 3;
  pointer-events: none;

  .landlordops-logo {
    width: 100% !important;
    height: 100% !important;
    max-width: none;
    max-height: none;
    filter: brightness(1) drop-shadow(0 0 20px rgba(255, 215, 0, 0.15))
      drop-shadow(0 2px 12px rgba(0, 0, 0, 0.1));
    display: block;
  }

  @media (max-width: 768px) {
    width: 100%;
    height: 100%;
  }
`;

const PageTitle = styled.h1`
  font-family:
    "Poppins",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  font-weight: 600;
  font-size: 4.1rem;
  color: #fff;
  margin: 0 0 2.2rem 0;
  letter-spacing: 0.045em;
  text-align: center;
  text-shadow:
    0 4px 18px rgba(0, 0, 0, 0.32),
    0 2px 8px rgba(212, 175, 55, 0.13);
  z-index: 3;
  @media (max-width: 768px) {
    font-size: 2.4rem;
    margin: 0 0 1.4rem 0;
  }
`;

const Subtitle = styled.p`
  font-family:
    "Inter",
    "Lato",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  font-weight: 400;
  font-size: 1.1rem;
  color: #fff;
  letter-spacing: 0.5px;
  text-align: center;
  margin: 0 auto 2.5rem auto;
  max-width: 600px;
  line-height: 1.8;
  z-index: 3;
  text-shadow:
    0 2px 8px rgba(0, 0, 0, 0.18),
    0 1px 4px rgba(212, 175, 55, 0.1);
  animation: fadeInUp 0.8s ease-out 0.2s both;
  width: 80%;

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    font-size: 0.85rem;
    margin-bottom: 1.5rem;
  }
`;

const HeroCTA = styled.a`
  display: inline-block;
  background: var(--color-text-white);
  color: var(--color-secondary);
  padding: 1rem 2rem;
  border-radius: 16px;
  text-decoration: none;
  font-weight: 700;
  font-size: 1.125rem;
  transition: all 0.3s ease;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  position: relative;
  z-index: 1;
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  margin-top: 2rem;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
    color: var(--color-secondary);
  }

  @media ${device.mobile} {
    padding: 0.875rem 1.75rem;
    font-size: 1rem;
  }
`;

const CTAButtonRow = styled.div`
  display: flex;
  gap: 2rem;
  justify-content: center;
  align-items: center;
  margin-top: 2.8rem;
  z-index: 3;
  @media (max-width: 600px) {
    flex-direction: column;
    gap: 1.3rem;
    margin-top: 1.8rem;
  }
`;

const PrimaryCTA = styled.a`
  background: #d4af37;
  color: #fff;
  border: none;
  padding: 1.25rem 3rem;
  border-radius: 9999px;
  font-size: 1.25rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 200px;
  box-shadow: 0px 4px 12px rgba(212, 175, 55, 0.4);
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  position: relative;
  overflow: hidden;
  letter-spacing: -0.01em;
  text-decoration: none;
  &:hover {
    background: #d4af37;
    color: #fff;
    box-shadow:
      0px 4px 12px rgba(212, 175, 55, 0.4),
      0px 0px 15px #ffd700;
    filter: brightness(1.1);
    transform: scale(1.05);
  }
  &:active {
    transform: scale(0.98);
  }
  @media (max-width: 600px) {
    min-width: auto;
    width: 100%;
    font-size: 1.15rem;
    padding: 1.2rem 1.5rem;
  }
`;

const SecondaryCTA = styled.a`
  background: #fff;
  color: #d4af37;
  border: 2px solid #d4af37;
  padding: 1.25rem 3rem;
  border-radius: 9999px;
  font-size: 1.25rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 200px;
  box-shadow: 0px 2px 8px rgba(212, 175, 55, 0.15);
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  position: relative;
  overflow: hidden;
  letter-spacing: -0.01em;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background: #d4af37;
    color: #fff;
    border: 2px solid #d4af37;
    box-shadow: 0px 4px 12px rgba(212, 175, 55, 0.4);
    filter: brightness(1.1);
    transform: scale(1.05);
  }
  @media (max-width: 600px) {
    min-width: auto;
    width: 100%;
    font-size: 1.15rem;
    padding: 1.2rem 1.5rem;
  }
`;

const ContentSection = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 5rem 2rem;

  @media ${device.mobile} {
    padding: 4rem 1.5rem;
  }
`;

const Section = styled.div`
  margin-bottom: 4rem;
  background: var(--color-bg-secondary);
  padding: 3.5rem;
  border-radius: 24px;
  box-shadow: 0 8px 32px var(--shadow-light);
  border: 1px solid var(--color-border-light);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--gradient-gold-luxury);
  }

  @media ${device.mobile} {
    padding: 2.5rem 1.5rem;
    margin-bottom: 3rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.75rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 2rem;
  text-align: center;
  font-family:
    "Poppins",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  position: relative;
  letter-spacing: -0.02em;

  &::after {
    content: "";
    position: absolute;
    bottom: -12px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: var(--gradient-gold-luxury);
    border-radius: 2px;
  }

  @media ${device.mobile} {
    font-size: 2.25rem;
    margin-bottom: 1.5rem;
  }
`;

const ProblemText = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 2rem;
  text-align: center;
  line-height: 1.4;
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  letter-spacing: -0.005em;

  @media ${device.mobile} {
    font-size: 1.25rem;
    margin-bottom: 1.5rem;
  }
`;

const SolutionText = styled.div`
  font-size: 1.375rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 2rem;
  text-align: center;
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  letter-spacing: -0.005em;

  @media ${device.mobile} {
    font-size: 1.25rem;
    margin-bottom: 1.5rem;
  }
`;

const BenefitsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2.5rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const BenefitItem = styled.div`
  font-size: 1.25rem;
  color: var(--color-text);
  padding: 1.25rem 1.5rem;
  text-align: center;
  background: var(--color-bg-tertiary);
  border-radius: 16px;
  border: 1px solid var(--color-border-light);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  letter-spacing: -0.005em;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px var(--shadow-medium);
    border-color: var(--color-primary-light);
  }

  @media ${device.mobile} {
    font-size: 1.125rem;
    padding: 1rem 1.25rem;
  }
`;

const ResultsText = styled.div`
  font-size: 1.375rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 1.5rem;
  text-align: center;
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  letter-spacing: -0.005em;

  @media ${device.mobile} {
    font-size: 1.25rem;
    margin-bottom: 1rem;
  }
`;

const ResultsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
`;

const ResultItem = styled.div`
  font-size: 1.25rem;
  color: var(--color-text);
  text-align: center;
  padding: 1rem 1.25rem;
  background: var(--color-bg-secondary);
  border-radius: 12px;
  border: 1px solid var(--color-border-light);
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  letter-spacing: -0.005em;

  @media ${device.mobile} {
    font-size: 1.125rem;
    padding: 0.875rem 1rem;
  }
`;

const Paragraph = styled.p`
  font-size: 1.125rem;
  line-height: 1.7;
  margin-bottom: 1.5rem;
  color: var(--color-text-light);
  text-align: left;
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  letter-spacing: -0.005em;

  @media ${device.mobile} {
    font-size: 1rem;
    line-height: 1.6;
    margin-bottom: 1.25rem;
  }

  em {
    font-style: italic;
    font-weight: 500;
  }
`;

const Emphasis = styled.span`
  font-weight: 700;
  color: var(--color-primary);
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 100%;
    height: 3px;
    background: var(--gradient-gold-luxury);
    border-radius: 2px;
  }
`;

const CTASection = styled.div`
  background: var(--gradient-gold-luxury);
  color: var(--color-text-white);
  padding: 4rem 3rem;
  border-radius: 28px;
  text-align: center;
  box-shadow: 0 20px 60px var(--shadow-gold);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.08'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    animation: float 20s ease-in-out infinite;
  }

  @keyframes float {
    0%,
    100% {
      transform: translateY(0px) rotate(0deg);
    }
    50% {
      transform: translateY(-20px) rotate(180deg);
    }
  }

  @media ${device.mobile} {
    padding: 3rem 2rem;
  }
`;

const CTATitle = styled.h2`
  font-size: 3rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  line-height: 1.2;
  position: relative;
  z-index: 1;
  font-family:
    "Poppins",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  color: white;
  letter-spacing: -0.02em;

  @media ${device.mobile} {
    font-size: 2.5rem;
    margin-bottom: 1rem;
  }
`;

const CTASubtitle = styled.p`
  font-size: 1.375rem;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 2.5rem;
  line-height: 1.6;
  position: relative;
  z-index: 1;
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  letter-spacing: -0.005em;

  @media ${device.mobile} {
    font-size: 1.25rem;
    margin-bottom: 2rem;
  }
`;

const CTABenefits = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 3rem;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  z-index: 1;
`;

const CTABenefit = styled.div`
  font-size: 1.125rem;
  color: rgba(255, 255, 255, 0.95);
  padding: 0.875rem 1.25rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  letter-spacing: -0.005em;

  @media ${device.mobile} {
    font-size: 1rem;
    padding: 0.75rem 1rem;
  }
`;

const CTAButton = styled.a`
  display: inline-block;
  background: var(--color-text-white);
  color: var(--color-primary);
  padding: 1.375rem 2.75rem;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 700;
  font-size: 1.25rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  position: relative;
  z-index: 1;
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  letter-spacing: -0.01em;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 12px 35px rgba(0, 0, 0, 0.3);
    color: var(--color-primary);
  }

  @media ${device.mobile} {
    padding: 1.125rem 2.25rem;
    font-size: 1.125rem;
  }
`;
